import { Botao } from './Botao.tsx';
import { Icone } from './Icone.tsx';

export const POR_PAGINA = 10;

/**
 * Recorta a página atual de uma lista já carregada. A página é limitada ao
 * intervalo válido aqui, então filtrar ou apagar itens nunca deixa a tela numa
 * página que deixou de existir.
 */
export function paginar<T>(itens: T[], pagina: number, porPagina = POR_PAGINA) {
  const totalPaginas = Math.max(1, Math.ceil(itens.length / porPagina));
  const atual = Math.min(Math.max(1, pagina), totalPaginas);
  const inicio = (atual - 1) * porPagina;
  return { itens: itens.slice(inicio, inicio + porPagina), pagina: atual, totalPaginas };
}

/** Números a mostrar: primeira, última e a vizinhança da atual, com reticências. */
function numeros(atual: number, total: number): (number | '…')[] {
  const mostrar = new Set([1, total, atual - 1, atual, atual + 1]);
  const ordenados = [...mostrar].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);

  const saida: (number | '…')[] = [];
  ordenados.forEach((n, i) => {
    const anterior = ordenados[i - 1];
    if (anterior !== undefined && n - anterior > 1) saida.push('…');
    saida.push(n);
  });
  return saida;
}

type Props = {
  total: number;
  pagina: number;
  aoMudar: (pagina: number) => void;
  porPagina?: number;
};

/** Paginação simples: anterior, números e próxima. Some quando cabe numa página só. */
export function Paginacao({ total, pagina, aoMudar, porPagina = POR_PAGINA }: Props) {
  if (total <= porPagina) return null;

  const totalPaginas = Math.ceil(total / porPagina);
  const atual = Math.min(Math.max(1, pagina), totalPaginas);

  const de = (atual - 1) * porPagina + 1;
  const ate = Math.min(atual * porPagina, total);

  return (
    <nav
      aria-label="Paginação"
      className="linha linha-entre"
      style={{ alignItems: 'center', gap: 'var(--gap-3)' }}
    >
      <span className="texto-fraco texto-sm">
        {de}–{ate} de {total}
      </span>

      <div className="linha" style={{ gap: 'var(--gap-1)', alignItems: 'center' }}>
        <Botao
          variante="fantasma"
          tamanho="pequeno"
          disabled={atual <= 1}
          onClick={() => aoMudar(atual - 1)}
          icone={<Icone.Voltar tamanho={14} />}
          aria-label="Página anterior"
        >
          Anterior
        </Botao>

        {numeros(atual, totalPaginas).map((n, i) =>
          n === '…' ? (
            <span key={`r${i}`} className="texto-fraco" aria-hidden="true">
              …
            </span>
          ) : (
            <Botao
              key={n}
              variante={n === atual ? 'primario' : 'fantasma'}
              tamanho="pequeno"
              aria-current={n === atual ? 'page' : undefined}
              aria-label={`Página ${n}`}
              onClick={() => aoMudar(n)}
            >
              {n}
            </Botao>
          ),
        )}

        <Botao
          variante="fantasma"
          tamanho="pequeno"
          disabled={atual >= totalPaginas}
          onClick={() => aoMudar(atual + 1)}
          iconeDireita={<Icone.Seta style={{ rotate: '-90deg' }} />}
          aria-label="Próxima página"
        >
          Próxima
        </Botao>
      </div>
    </nav>
  );
}
